import { DoneActorEvent, TODO, assign, enqueueActions, fromPromise, setup } from 'xstate';

export interface CreatePollingMachineOptions<T> {
  interval: number;
  maxRetries: number;
  promiseFn: () => Promise<T>;
  shouldResolve: (response: T) => boolean;
  shouldReject?: (response: T) => boolean;
}

export interface PollingMachineContext<T> extends CreatePollingMachineOptions<T> {
  retries: number;
}

export function createPollingMachine<T>(options: { id: string }) {
  return setup({
    types: {
      output: {} as T,
      input: {} as CreatePollingMachineOptions<T>,
      context: {} as PollingMachineContext<T>,
    },

    delays: {
      WAIT: ({ context }) => context.interval,
    },

    actions: {
      incrementRetriesCount: assign({ retries: ({ context }) => context.retries + 1 }),
    },

    guards: {
      canRetry: ({ context }, params: { retries: number }) => params.retries < context.maxRetries,
      shouldReject: ({ context }, data: T) => context.shouldReject?.(data) ?? false,
      shouldResolve: ({ context }, data: T) => context.shouldResolve(data),
    },

    actors: {
      fetcher: fromPromise<T, { promiseFn: () => Promise<T> }>(({ input }) => input.promiseFn()),
    },
  }).createMachine({
    id: options.id,

    context: ({ input }) => {
      return {
        maxRetries: input.maxRetries,
        interval: input.interval,
        promiseFn: input.promiseFn,
        shouldResolve: input.shouldResolve,
        shouldReject: input.shouldReject,
        retries: 0,
      };
    },

    output: ({ event }) => event.output as T,

    initial: 'polling',

    states: {
      polling: {
        entry: [{ type: 'incrementRetriesCount' }],
        invoke: {
          src: 'fetcher',
          input: ({ context }) => ({ promiseFn: context.promiseFn }),
          onDone: [
            { guard: { type: 'shouldReject' as TODO, params: ({ event }) => event.output }, target: 'error' },
            { guard: { type: 'shouldResolve' as TODO, params: ({ event }) => event.output }, target: 'done' },
            { target: 'waiting' },
          ],
          onError: [
            { guard: { type: 'canRetry', params: ({ context }) => ({ retries: context.retries }) }, target: 'waiting' },
            { target: 'error' },
          ],
        },
      },

      waiting: {
        after: {
          WAIT: { target: 'polling' },
        },
      },

      done: {
        type: 'final',
        output: ({ event }) => (event as DoneActorEvent<T>).output,
      },

      error: {
        type: 'final',
        entry: [
          enqueueActions(({ event }) => {
            throw (event as DoneActorEvent<T>).output;
          }),
        ],
      },
    },
  });
}
